import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import LotTaxSettings from "@/components/LotTaxSettings";
import GatewaySettings from "@/components/GatewaySettings";
import TermsSettings from "@/components/TermsSettings";
import { sizeLabel, sizeValue } from "@/lib/sizeLabels";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sizeText, setSizeText] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setS(data);
      setSizeText((data.sizeClasses || []).map(sizeLabel).join(", "));
    });
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        dailyRate: Number(s.dailyRate),
        monthlyRate: Number(s.monthlyRate),
        gracePeriodMinutes: Number(s.gracePeriodMinutes),
        cancellationWindowHours: Number(s.cancellationWindowHours),
        overstayPolicy: s.overstayPolicy,
        businessName: s.businessName,
        businessPhone: s.businessPhone,
        sizeClasses: sizeText.split(",").map((x) => sizeValue(x.trim())).filter(Boolean),
        loyaltyEnabled: !!s.loyaltyEnabled,
        earnMode: s.earnMode || "perNight",
        earnRate: Number(s.earnRate),
        redemptionThreshold: Number(s.redemptionThreshold),
        tierSilverNights: Number(s.tierSilverNights),
        tierGoldNights: Number(s.tierGoldNights),
        silverDiscountPct: Number(s.silverDiscountPct),
        goldDiscountPct: Number(s.goldDiscountPct),
      };
      const { data } = await api.put("/settings", payload);
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!s) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-800">
          <SlidersHorizontal className="h-6 w-6 text-[#A0148C]" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-50">Settings</h1>
          <p className="text-slate-400">Rates, policies, and business info.</p>
        </div>
      </div>

      <div className="grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-slate-800 bg-slate-900 p-6">
          <h2 className="mb-4 font-heading text-xl font-bold text-slate-100">Rates & Policy</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Daily Rate ($)" testid="settings-daily" value={s.dailyRate} onChange={(v) => set("dailyRate", v)} />
            <Field label="Monthly Rate ($)" testid="settings-monthly" value={s.monthlyRate} onChange={(v) => set("monthlyRate", v)} />
            <Field label="Grace Period (min)" testid="settings-grace" value={s.gracePeriodMinutes} onChange={(v) => set("gracePeriodMinutes", v)} />
            <Field label="Cancellation Window (hrs)" testid="settings-cancel" value={s.cancellationWindowHours} onChange={(v) => set("cancellationWindowHours", v)} />
          </div>
          <div className="mt-4 space-y-2">
            <Label className="text-slate-300">Overstay Policy</Label>
            <Input
              data-testid="settings-policy"
              value={s.overstayPolicy || ""}
              onChange={(e) => set("overstayPolicy", e.target.value)}
              className="h-12 bg-slate-950 border-slate-700"
            />
          </div>
        </Card>

        <Card className="border-slate-800 bg-slate-900 p-6">
          <h2 className="mb-4 font-heading text-xl font-bold text-slate-100">Business & Taxonomy</h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Business Name</Label>
              <Input
                data-testid="settings-bizname"
                value={s.businessName || ""}
                onChange={(e) => set("businessName", e.target.value)}
                className="h-12 bg-slate-950 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Business Phone</Label>
              <Input
                data-testid="settings-bizphone"
                value={s.businessPhone || ""}
                onChange={(e) => set("businessPhone", e.target.value)}
                className="h-12 bg-slate-950 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Spot Size Classes (comma separated)</Label>
              <Input
                data-testid="settings-sizes"
                value={sizeText}
                onChange={(e) => setSizeText(e.target.value)}
                className="h-12 bg-slate-950 border-slate-700"
              />
            </div>
          </div>
        </Card>

        <LotTaxSettings />

        <GatewaySettings />

        <TermsSettings />

        {/* Loyalty */}
        <Card className="border-slate-800 bg-slate-900 p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-slate-100">Loyalty Program</h2>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                data-testid="settings-loyalty-enabled"
                type="checkbox"
                checked={!!s.loyaltyEnabled}
                onChange={(e) => set("loyaltyEnabled", e.target.checked)}
                className="h-5 w-5 accent-[#A0148C]"
              />
              Enabled
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Earn Mode</Label>
              <select
                data-testid="settings-loyalty-earnmode"
                value={s.earnMode || "perNight"}
                onChange={(e) => set("earnMode", e.target.value)}
                className="h-12 w-full rounded-md border border-slate-700 bg-slate-950 px-3 text-slate-100"
              >
                <option value="perNight">Per night</option>
                <option value="perDollar">Per dollar</option>
              </select>
            </div>
            <Field label="Earn Rate" testid="settings-loyalty-earnrate" value={s.earnRate} onChange={(v) => set("earnRate", v)} />
            <Field label="Free Night @ (pts)" testid="settings-loyalty-threshold" value={s.redemptionThreshold} onChange={(v) => set("redemptionThreshold", v)} />
            <Field label="Silver @ (nights)" testid="settings-loyalty-silver" value={s.tierSilverNights} onChange={(v) => set("tierSilverNights", v)} />
            <Field label="Gold @ (nights)" testid="settings-loyalty-gold" value={s.tierGoldNights} onChange={(v) => set("tierGoldNights", v)} />
            <Field label="Silver Discount (%)" testid="settings-loyalty-silverdisc" value={s.silverDiscountPct} onChange={(v) => set("silverDiscountPct", v)} />
            <Field label="Gold Discount (%)" testid="settings-loyalty-golddisc" value={s.goldDiscountPct} onChange={(v) => set("goldDiscountPct", v)} />
          </div>
        </Card>
      </div>

      <Button
        data-testid="settings-save"
        onClick={save}
        disabled={saving}
        className="mt-6 h-14 gap-2 bg-[#A0148C] px-8 text-lg font-bold hover:bg-[#80106F]"
      >
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
        Save Settings
      </Button>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300">{label}</Label>
      <Input
        data-testid={testid}
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 bg-slate-950 border-slate-700 font-data"
      />
    </div>
  );
}
